import React, { useEffect } from 'react';
import * as Sentry from '@sentry/react';

// The boundary owns the entire panel, including its background. A failed row
// must not leave an empty overlay covering a live hand.
export class TablePanelBoundary extends React.Component<
  { onClose: () => void; children: React.ReactNode },
  { failed: boolean }
> {
  state = { failed: false };

  static getDerivedStateFromError() {
    return { failed: true };
  }

  componentDidCatch(error: Error, info: React.ErrorInfo) {
    try {
      Sentry.captureException(error, { extra: { componentStack: info.componentStack } });
    } catch {
      // Telemetry failure must not turn a panel failure into a table failure.
    } finally {
      this.props.onClose();
    }
  }

  render() {
    return this.state.failed ? null : this.props.children;
  }
}

// Closes an open panel on Escape, or when the app is backgrounded/unloaded.
// Outside taps are handled by hooks/useOutsideTapDismiss, which owns touch slop,
// the launcher toggle, the action-bar swallow and grid-cell scoping.
export const useTablePanelDismiss = (open: boolean, onClose: () => void) => {
  useEffect(() => {
    if (!open) return;
    const escape = (event: KeyboardEvent) => {
      if (event.key === 'Escape') onClose();
    };
    const hide = () => {
      if (document.visibilityState === 'hidden') onClose();
    };
    document.addEventListener('keydown', escape);
    document.addEventListener('visibilitychange', hide);
    window.addEventListener('pagehide', onClose);
    return () => {
      document.removeEventListener('keydown', escape);
      document.removeEventListener('visibilitychange', hide);
      window.removeEventListener('pagehide', onClose);
    };
  }, [open, onClose]);
};
